import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Button, Pagination, Table, Tag} from 'antd'
import queryString from 'query-string'
import {getOutputSuspects} from '../../../../services/api/apiProductionPlanning'
import {formatInt, formatVnDateTime, formatVnTime} from '../../helpers/format'
import Notice from '../shared/Notice'
import LoadError from '../shared/LoadError'

/**
 * SCR-M18-003 — Cần kiểm tra ghi nhận.
 *
 * Danh sách này để chuyển cho Dev kiểm tra ghi nhận thừa. Nó **không** làm thay đổi số liệu ở màn
 * hình báo cáo: sản lượng vẫn tính đủ cho mọi dòng log.
 */
/**
 * `embedded` = đang nằm trong modal của màn báo cáo.
 *
 * Nhúng thay vì điều hướng sang trang riêng: đây là bước KHOAN SÂU từ một ô đã bấm, người dùng cần
 * quay lại đúng bối cảnh cũ ngay. Rời trang thì bộ lọc, vị trí cuộn và các dòng đang mở của báo cáo
 * đều mất, phải dựng lại từ đầu chỉ để xem một danh sách phụ.
 *
 * Vẫn giữ nguyên `useUrlFilters` ở màn báo cáo nên đường dẫn vẫn mang đủ tham số — copy link gửi
 * người khác vẫn mở đúng modal này với đúng bộ lọc.
 */
function SuspectPage({history, location, embedded, params, onClose}) {
    // Nhánh nhúng lấy tham số từ props; nhánh trang độc lập vẫn đọc query như cũ, để đường dẫn cũ
    // (link đã chia sẻ trước đây) không hỏng.
    const query = embedded ? params : queryString.parse(location.search)

    const [rows, setRows] = useState([])
    const [pagination, setPagination] = useState({page: 1, limit: 50, total: 0})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    // Cờ này gác CẢ HAI trục, nên phải bật khi có BẤT KỲ trục nào — không chỉ `actor`.
    // Màn gộp theo công đoạn (SCR-M18-005) khoan sâu từ dòng cha với `actor` rỗng và chỉ có
    // `stageCode`; lấy mình `!!query.actor` thì cờ tắt, `stageCode` bị vứt ở `fetchRows` bên dưới,
    // và danh sách trả về của TOÀN BỘ báo cáo — trong khi thẻ lọc cũng không hiện nên không có
    // dấu hiệu nào cho người dùng biết phạm vi đã bị bỏ.
    const [hasChip, setHasChip] = useState(!!query.actor || !!query.stageCode)

    const fetchRows = useCallback(async (page = 1) => {
        setLoading(true)
        setError(null)

        try {
            const result = await getOutputSuspects({
                from: query.from,
                to: query.to,
                actor: hasChip ? query.actor : undefined,
                stageCode: hasChip ? query.stageCode : undefined,
                page,
            })

            setRows(result.data || [])
            setPagination(result.pagination || {page, limit: 50, total: 0})
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasChip, query.from, query.to, query.actor, query.stageCode])

    useEffect(() => {
        fetchRows(1)
    }, [fetchRows])

    const columns = [
        {
            title: 'Barcode',
            dataIndex: 'barcode',
            width: 150,
            render: (value) => <code className="VcBarcode">{value}</code>,
        },
        {title: 'Đơn hàng', dataIndex: 'orderId', width: 120},
        {title: 'SKU', dataIndex: 'sku', width: 130, render: (value) => <code>{value}</code>},
        {title: 'Tài khoản', dataIndex: 'actor', width: 130},
        {title: 'Công đoạn', dataIndex: 'stageName', width: 130},
        {
            title: 'Số lần ghi nhận',
            dataIndex: 'logCount',
            align: 'right',
            width: 130,
            render: (value) => <Tag color="red">{formatInt(value)}</Tag>,
        },
        {
            // Đếm trên MỌI tài khoản, không chỉ tài khoản đang lọc (D-AC-02).
            title: 'Số tài khoản liên quan',
            dataIndex: 'actorCount',
            align: 'right',
            width: 150,
            render: formatInt,
        },
        {
            /*
             * Cột giúp phân loại nhanh: cách nhau vài giây thì nhiều khả năng là lỗi kỹ thuật, cách
             * nhau nhiều giờ thì nhiều khả năng là hàng làm lại thật. Không có cột này thì mọi dòng
             * trông giống nhau và Dev không biết bắt đầu từ đâu.
             */
            title: 'Khoảng thời gian',
            key: 'range',
            /*
             * ⚠️ Giờ VN. `formatTime` đọc đồng hồ MÁY, mà bảng này gom theo NGÀY VN — người xem từ
             *    múi khác đọc khoảng thời gian của một dòng lệch hẳn khỏi ngày chứa nó.
             *    (Vòng review 7, #12.)
             */
            render: (text, row) => `${formatVnTime(row.firstLogTime)} → ${formatVnTime(row.lastLogTime)}`,
        },
    ]

    return (
        <div className={embedded ? 'VcEmbeddedPage' : 'VcPage VcSuspectPage container-fluid'}>
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">Cần kiểm tra ghi nhận</h1>
                    <div className="VcPageSubtitle">
                        Barcode xuất hiện quá 3 lần
                        {/*
                          * ⚠️ Giờ VN, cùng đồng hồ với cột "Khoảng thời gian" của từng dòng. Để
                          *    phụ đề theo giờ MÁY trong khi các dòng theo giờ VN là lại dựng hai
                          *    đồng hồ trên một màn — đúng thứ vừa gỡ ở chỗ kia.
                          *    (Vòng review ngoài 3, G-FE2.)
                          */}
                        {query.from && ` · ${formatVnDateTime(query.from)} → ${formatVnDateTime(query.to)} (giờ VN)`}
                    </div>
                </div>
                <div className="VcPageActions">
                    {embedded ? (
                        <Button onClick={onClose}>Đóng</Button>
                    ) : (
                        <Button onClick={() => history.goBack()}>← Quay lại báo cáo</Button>
                    )}
                </div>
            </div>

            {hasChip && (
                <div className="VcChipRow">
                    <Tag closable onClose={() => setHasChip(false)}>
                        {[query.actor, query.stageCode].filter(Boolean).join(' · ')}
                    </Tag>
                </div>
            )}

            <Notice level="warning">
                <div>
                    Danh sách này không làm thay đổi số liệu ở màn hình báo cáo, sản lượng vẫn tính đủ
                    cho mọi dòng log.
                </div>
                <div>Đây là danh sách chuyển cho Dev kiểm tra ghi nhận thừa.</div>
                <div>
                    Số lần chỉ đếm trong khoảng thời gian đang lọc, lọc khoảng rộng hơn sẽ ra nhiều dòng
                    hơn.
                </div>
            </Notice>

            {error ? (
                <LoadError error={error} onRetry={() => fetchRows(pagination.page)}/>
            ) : (
                <>
                    <Table
                        className="VcTable"
                        rowKey={(row) => `${row.barcode}-${row.stageCode}`}
                        columns={columns}
                        dataSource={rows}
                        loading={loading}
                        pagination={false}
                        size="middle"
                        locale={{
                            /*
                             * G-04: trạng thái rỗng dùng màu trung tính, KHÔNG dùng màu lỗi — không có
                             * barcode nào vượt ngưỡng là kết quả tốt.
                             */
                            emptyText: (
                                <div className="VcEmptyState">
                                    <div>Không có barcode nào vượt ngưỡng.</div>
                                    <div className="VcHint">
                                        Mọi barcode trong khoảng lọc đều được ghi nhận không quá 3 lần.
                                    </div>
                                </div>
                            ),
                        }}
                    />

                    <div className="VcPaginationBar">
                        <span className="VcMuted">
                            Hiển thị {formatInt((pagination.page - 1) * pagination.limit + (rows.length ? 1 : 0))}
                            –{formatInt((pagination.page - 1) * pagination.limit + rows.length)} trong{' '}
                            {formatInt(pagination.total)} dòng
                        </span>
                        <Pagination
                            current={pagination.page}
                            pageSize={pagination.limit}
                            total={pagination.total}
                            onChange={fetchRows}
                        />
                    </div>
                </>
            )}
        </div>
    )
}

SuspectPage.propTypes = {
    embedded: PropTypes.bool,
    params: PropTypes.object,
    onClose: PropTypes.func,
}

SuspectPage.defaultProps = {embedded: false, params: {}, onClose: () => undefined}

export default SuspectPage
